//  #region Imports

/* HLS.js */
import * as Hls from '@user-interface/hls.js-bugfixed';

/* Telestream */
import { MetadataService, TimecodeService } from '@user-interface/glim-player';
import { Events } from '@user-interface/glim-player/lib/VideoController';
import {
  ADD_SUBTITLE_SOURCE,
  ADD_TO_HISTORY,
  ADJUST_VIDEO_QUALITY,
  ADJUST_VOLUME,
  ATTACH_FULLSCREEN_ELEMENT,
  ATTACH_VIDEO_ELEMENT,
  CHANGE_PLAYBACK_RATE,
  CLEAR_MARK_IN,
  CLEAR_MARK_OUT,
  COPY_MEDIA_PATH,
  END_INDEXING_OBSERVATION,
  EXIT_FULLSCREEN,
  FAST_FORWARD,
  FETCH_PLUGIN_SCHEMAS,
  HANDLE_LINKED_MEDIA,
  JUMP_BACK_FRAMES,
  JUMP_FORWARD_FRAMES,
  JUMP_TO_BEGINNING,
  JUMP_TO_END,
  JUMP_TO_FRAME,
  JUMP_TO_NEXT_MARKER,
  JUMP_TO_PREVIOUS_MARKER,
  JUMP_TO_NEXT_IFRAME,
  JUMP_TO_PREVIOUS_IFRAME,
  JUMP_TO_TIME,
  LOAD_VIDEO,
  MARK_IN,
  MARK_OUT,
  MODIFY_AUDIO_SOUND_FIELD,
  OBSERVE_INDEXING,
  PAUSE_VIDEO,
  PLAY_VIDEO,
  REMOVE_SUBTITLE_SOURCE,
  RESET_VIDEO_STATE,
  REWIND,
  SELECT_AUDIO_TRACK,
  SELECT_FIRST_TIMECODE_TRACK,
  SELECT_NEXT_SUBTITLE_SOURCE,
  SELECT_OVERLAYS,
  SELECT_PERSISTED_SUBTITLE_SOURCE,
  SELECT_SUBTITLE_SOURCE,
  SELECT_TIME_DISPLAY_SOURCE,
  SELECT_WAVEFORM_MARKS,
  SHOW_FULLSCREEN,
  SIMULATE_ALPHA,
  SLOW_DOWN,
  SPEED_UP,
  START_SCOPES,
  STOP_SCOPES,
  STOP_WATCHING_FOR_ANNOTATIONS,
  TOGGLE_MUTE,
  TOGGLE_REPEAT,
  TURN_OFF_SUBTITLES,
  UNLOAD_VIDEO,
  WATCH_FOR_ANNOTATIONS,
  SELECT_ROTATION,
} from '@/utils/action-types';
import {
  SET_AUDIO_PATHS_CONFIG,
  SET_CURRENT_SIDECAR_AUDIO_PATHS,
  SET_FILE_BROWSER_IS_HIDDEN,
  SET_FILE_BROWSER_IS_OPEN,
  SET_FPS,
  SET_FRAME,
  SET_FRAME_METADATA,
  SET_FULLSCREEN_ELEMENT,
  SET_MEDIA_PROPERTIES,
  SET_PLUGIN_SCHEMAS,
  SET_ROTATION,
  SET_SELECTED_WAVEFORM_MARKS,
  SET_SHOW_TIMECODE_OVERLAY,
  SET_SNACK,
  SET_TIME_DISPLAY_SOURCE,
  SET_VIDEO_CAN_PLAY,
  SET_VIDEO_CONTROLLER,
  SET_VIDEO_CURRENT_TIME,
  SET_VIDEO_CURRENT_TIMECODE,
  SET_VIDEO_DURATION,
  SET_VIDEO_ELEMENT,
  SET_VIDEO_FIRST_FRAME,
  SET_VIDEO_FIRST_TIMECODE,
  SET_VIDEO_FRAME_COUNT,
  SET_VIDEO_HAS_FAILED_LOADING,
  SET_VIDEO_IN,
  SET_VIDEO_IS_FULLSCREEN,
  SET_VIDEO_IS_LIVE,
  SET_VIDEO_IS_LOADING,
  SET_VIDEO_IS_MUTED,
  SET_VIDEO_IS_ON_REPEAT,
  SET_VIDEO_IS_PAUSED,
  SET_VIDEO_IS_REWINDING,
  SET_VIDEO_LAST_TIMECODE,
  SET_VIDEO_OPEN,
  SET_VIDEO_OUT,
  SET_VIDEO_OVERLAYS,
  SET_VIDEO_PATH,
  SET_VIDEO_PLAYBACK_RATE,
  SET_VIDEO_SIMULATE_ALPHA,
  SET_VIDEO_TIMECODE_SOURCES,
  SET_VIDEO_VOLUME,
} from '@/utils/mutation-types';
import { BusService } from '@/services/bus.service';
import { ConnectionService } from '../services/connection.service';
import {
  extractAudioPropsFromQueryParams,
  extractAutoPlayPropsFromQueryParams,
  extractCaptionPropsFromQueryParams,
  extractFileBrowserPropsFromQueryParams,
  extractVideoPropsFromQueryParams,
  generateBaseUrl,
  generateClipLink,
  generateMasterManifestUrl,
} from './utilities/connection.utils';
import {
  fetchMediaProperties,
  generateController,
  handleFatalNetworkError,
  isImage,
  onHlsError
} from './utilities/media.utils';
import {
  restoreIsMuted,
  restoreOverlays,
  restoreSelectedTimeDisplaySource,
  restoreSelectedWaveformMarks,
  restoreSimulateAlpha,
  restoreSubtitleSource,
  restoreVideoQualityLevel,
  restoreVolume,
  storeIsMuted,
  storeOverlays,
  storeSelectedWaveformMarks,
  storeSimulateAlpha,
  storeSubtitleSource,
  storeVideoQualityLevel,
  storeVolume,
} from './utilities/persistence.utils';
import { getFirstTimecode, getLastTimecode, getTimecodeSources } from './utilities/timecodes.utils';
import { LEVELS, sendEphemeralSnack, sendVideoLoadedSnack } from './utilities/snacks.utils';
import {
  AUDIO_PRIMING_DELAY_IN_SECONDS,
  GLIM_TIMECODE_SOURCE
} from '@/utils/constants';
import { copyUsingTextArea } from '@/store/utilities/clipboard.utils';
import { findNextMarkerTime, findPreviousMarkerTime, findNextIFrameTime, findPreviousIFrameTime } from '@/store/utilities/markers.utils';
import {searchForCue} from "@user-interface/glim-player/lib/services/metadata.service";
import {clearMeters} from "@/store/audio.store";

// #endregion Imports

// #region Constants

const PLAYBACK_RATE_LIST = [1, 2, 3, 5, 8, 16];   // Same rates for forward and rewind.

// #endregion Constants

export const video = {
  // #region State

  state: {
    element:               undefined,
    fullscreenElement:     undefined,
    properties:            undefined,
    isLoading:             false,
    hasFailedLoading:      false,
    canPlay:               false,
    currentTime:           0,
    currentTimecode:       undefined,
    startFrame:            undefined,
    currentFrame:          0,
    duration:              0,
    frameCount:            undefined,
    framerate:             0,
    playbackRate:          1,
    paused:                true,
    isOnRepeat:            false,
    isRewinding:           false,
    isMuted:               false,
    volume:                1,
    showTimecodeOverlay:   false,
    isLive:                false,
    isAlphaSimulated:      restoreSimulateAlpha(),
    firstTimecode:         undefined,
    lastTimecode:          undefined,
    inMarker:              undefined,
    outMarker:             undefined,
    timecodeSources:       [],
    overlays:              restoreOverlays(),
    frameMetadata:         [],
    isFullscreen:          false,
    selectedWaveformMarks: [],
    rotation:              'default'
  },

  // #endregion State

  // #region Mutations

  mutations: {
    [SET_VIDEO_ELEMENT](state, element) {
      state.element = element;
    },

    [SET_FULLSCREEN_ELEMENT](state, element) {
      state.fullscreenElement = element;
    },

    [SET_MEDIA_PROPERTIES](state, properties) {
      state.properties = properties;
    },

    [SET_VIDEO_IS_LOADING](state, isLoading) {
      state.isLoading = isLoading;
    },

    [SET_VIDEO_HAS_FAILED_LOADING](state, hasFailedLoading) {
      state.hasFailedLoading = hasFailedLoading;
    },

    [SET_VIDEO_CAN_PLAY](state, canPlay) {
      state.canPlay = canPlay;
    },

    [SET_VIDEO_CURRENT_TIME](state, time) {
      state.currentTime = time;
    },

    [SET_VIDEO_CURRENT_TIMECODE](state, current) {
      state.currentTimecode = current.timecode;
      state.currentFrame = current.frame;
    },

    [SET_VIDEO_TIMECODE_SOURCES](state, sources) {
      state.timecodeSources = sources;
    },

    [SET_FRAME](state, frame) {
      state.currentFrame = frame;
    },

    [SET_FPS](state, framerate) {
      state.framerate = framerate;
    },

    [SET_VIDEO_DURATION](state, duration) {
      state.duration = duration;
    },

    [SET_VIDEO_FIRST_TIMECODE](state, timecode) {
      state.firstTimecode = timecode;
    },

    [SET_VIDEO_LAST_TIMECODE](state, timecode) {
      state.lastTimecode = timecode;
    },

    [SET_VIDEO_IN](state, inMarker) {
      state.inMarker = inMarker;
    },

    [SET_VIDEO_OUT](state, outMarker) {
      state.outMarker = outMarker;
    },

    [SET_VIDEO_FIRST_FRAME](state, firstFrame) {
      state.firstFrame = firstFrame;
    },

    [SET_VIDEO_FRAME_COUNT](state, frameCount) {
      state.frameCount = frameCount;
    },

    [SET_VIDEO_IS_PAUSED](state, isPaused) {
      state.paused = isPaused;
    },

    [SET_VIDEO_PLAYBACK_RATE](state, rate) {
      state.playbackRate = rate;
    },

    [SET_VIDEO_IS_ON_REPEAT](state, isOnRepeat) {
      state.isOnRepeat = isOnRepeat;
    },

    [SET_VIDEO_IS_REWINDING](state, isRewinding) {
      state.isRewinding = isRewinding;
    },

    [SET_VIDEO_IS_MUTED](state, isMuted) {
      state.isMuted = isMuted;
    },

    [SET_VIDEO_VOLUME](state, volume) {
      state.volume = volume;
    },

    [SET_SHOW_TIMECODE_OVERLAY](state, showOverlay) {
      state.showTimecodeOverlay = showOverlay;
    },

    [SET_VIDEO_OVERLAYS](state, overlays) {
      state.overlays = overlays;
    },

    [SET_VIDEO_SIMULATE_ALPHA](state, isSimulated) {
      state.isAlphaSimulated = isSimulated;
    },

    [SET_VIDEO_IS_LIVE](state, isLive) {
      state.isLive = isLive;
    },

    [SET_FRAME_METADATA](state, metadata) {
      state.frameMetadata = metadata;
    },

    [SET_VIDEO_IS_FULLSCREEN](state, isFullscreen) {
      state.isFullscreen = isFullscreen;
    },
    [SET_SELECTED_WAVEFORM_MARKS](state, selectedWaveformMarks) {
      state.selectedWaveformMarks = selectedWaveformMarks;
    },
    [SET_ROTATION](state, rotation) {
      state.rotation = rotation;
    },
  },

  // #endregion Mutations

  // #region Actions

  actions: {
    [ATTACH_VIDEO_ELEMENT](context, element) {
      element.muted = restoreIsMuted();
      element.volume = restoreVolume();

      context.commit(SET_SELECTED_WAVEFORM_MARKS, restoreSelectedWaveformMarks());
      context.commit(SET_VIDEO_ELEMENT, element);
      context.commit(SET_VIDEO_DURATION, element.duration);
      context.commit(SET_VIDEO_PLAYBACK_RATE, element.playbackRate);
      context.commit(SET_VIDEO_VOLUME, element.volume);
      context.commit(SET_VIDEO_IS_ON_REPEAT, element.loop);
      context.commit(SET_VIDEO_IS_MUTED, element.muted);

      element.onloadstart = () => context.commit(SET_VIDEO_CAN_PLAY, false);
      element.oncanplay = () => {
        context.commit(SET_VIDEO_CAN_PLAY, true);
        context.commit(SET_FPS, TimecodeService.getFrameRateFromGlimTimecode(context.state.element));

        const sourceName = context.rootState.timeDisplaySource.name;
        context.commit(SET_VIDEO_FIRST_TIMECODE, getFirstTimecode(context.state.element, sourceName));
        context.commit(SET_VIDEO_LAST_TIMECODE, getLastTimecode(context.state.element, sourceName));
      };
      element.onpause = () => context.commit(SET_VIDEO_IS_PAUSED, true);
      element.onplay = () => context.commit(SET_VIDEO_IS_PAUSED, false);
      element.ondurationchange = () => {
        context.commit(SET_VIDEO_DURATION, element.duration);

        const sourceName = context.rootState.timeDisplaySource.name;
        context.commit(SET_VIDEO_LAST_TIMECODE, getLastTimecode(context.state.element, sourceName));
      }
      element.onplaybackRate = () => context.commit(SET_VIDEO_PLAYBACK_RATE, element.playbackRate);
      element.onvolumechange = () => context.commit(SET_VIDEO_VOLUME, element.volume);

      pollVideoElement(undefined, context, element);
      // If the user navigates to another page and back:
      if (context.rootState.videoPath) {
        context.dispatch(
          LOAD_VIDEO,
          {
            videoPath:     context.rootState.videoPath,
            audioPaths:    context.rootState.currentSidecarAudioPaths,
            subtitlePaths: context.rootState.videoController.sidecarSubtitles.map(s => s.path),
            startFrame:    context.state.firstFrame,
            frameCount:    context.state.frameCount,
            open:          context.rootState.videoOpen
          }
        );
      }
    },

    [ATTACH_FULLSCREEN_ELEMENT](context, element) {
      context.commit(SET_FULLSCREEN_ELEMENT, element);
      element.onfullscreenchange = () => {
        context.commit(SET_VIDEO_IS_FULLSCREEN, document.fullscreenElement === element);
      };
    },

    [ADJUST_VIDEO_QUALITY](context, level) {
      if (!context.rootState.videoController) {
        return;
      }

      // We remove the alpha part from the name to not pollute the persisted value.
      const levelName = level.name.replace(' Alpha channel', '');
      context.rootState.videoController.setVideoQuality(levelName, context.state.isAlphaSimulated);
      storeVideoQualityLevel(levelName);
    },

    [HANDLE_LINKED_MEDIA](context) {
      const filebrowserProps = extractFileBrowserPropsFromQueryParams();
      context.commit(SET_FILE_BROWSER_IS_HIDDEN, filebrowserProps.isHidden);

      const urlVideoProps = extractVideoPropsFromQueryParams();
      if (urlVideoProps.videoPath) {
        /*
         * If the audio props are applied immediately after loading the video,
         * the media never fully binds to the element. Therefore, we wait one time
         * for the video to be ready to play and apply the settings then.
         */
        context.state.element.addEventListener(
          'canplay',
          async () => {
            const urlAudioProps = extractAudioPropsFromQueryParams();
            if (urlAudioProps.soundField) {
              context.dispatch(SELECT_AUDIO_TRACK, urlAudioProps.track);
              context.dispatch(MODIFY_AUDIO_SOUND_FIELD, urlAudioProps);
            }

            const captionProps = extractCaptionPropsFromQueryParams();
            if (captionProps.subtitles.length === 0) {
              return;
            }

            /*
             * The url can include one or more subtitles. However, this can be a mix
             * of sidecar subtitle files, a baked in subtitle track, and the value "Off".
             * 
             * The client will select the very first track in the url, but we must first
             * add all sidecar files, except for the very first, since adding a sidecar
             * subtitle file automatically selects it.
             */
            const sidecarFilesLoaded = [];
            const firstItem = captionProps.subtitles.shift();
            for (let subtitleIndex = 0; subtitleIndex < captionProps.subtitles.length; subtitleIndex++) {
              const subtitle = captionProps.subtitles[subtitleIndex];
              if (subtitle !== 'Off') {
                try {
                  await context.rootState.videoController.addSidecarSubtitles(subtitle);
                  sidecarFilesLoaded.push(subtitle);
                }

                catch { /* Only side car files after the first instance of the query parameter. */ }
              }
            }

            if (firstItem === 'Off') {
              context.dispatch(TURN_OFF_SUBTITLES);
            } else {
              /*
               * Besides 'Off', subs can take one of two forms: baked-in subtitle
               * tracks, or sidecar subtitle files. Since there is no good way to
               * see if what is specified is a baked-in track (these can appear at 
               * any time after the clip has been loaded), we have to try and load
               * it as a sidecar file, and if that fails, assume it is a baked-in
               * track. 
               */
              try {
                await context.rootState.videoController.addSidecarSubtitles(firstItem);
                sidecarFilesLoaded.push(firstItem);
              }

              catch {
                context.rootState.videoController.setHlsSubtitles(firstItem);
              }

              storeSubtitleSource(context.rootState.videoController.currentSubtitles);
            }

            if (sidecarFilesLoaded.length > 0) {
              context.commit(SET_SNACK, {
                message: `Added subtitles from ${sidecarFilesLoaded.join(', ').replace(/,([^,]*)$/, ' and$1')}.`,
                level:   'success',
              });
            }

            if (extractAutoPlayPropsFromQueryParams().autoplay) {
              context.dispatch(PLAY_VIDEO);
            }
          },
          {
            once: true,
          }
        );
        context.dispatch(LOAD_VIDEO, urlVideoProps);
      }
    },

    async [LOAD_VIDEO](context, { videoPath, audioPaths, subtitlePaths, startFrame, frameCount, playheadOffset, open, token }) {
      if (context.state.rotation){
        context.state.element.style.transform = 'none'
      }
      if (context.rootState.videoController) {
        await context.dispatch(RESET_VIDEO_STATE);
        await context.dispatch(END_INDEXING_OBSERVATION);
      }
      let fileOpen = open? open : false;
      context.commit(SET_VIDEO_IS_LOADING, true);
      context.commit(SET_FILE_BROWSER_IS_OPEN, false); // TODO This could be moved to handle load failures.

      const baseUrl = generateBaseUrl(videoPath, audioPaths, startFrame, frameCount, token, context.state.element, fileOpen);

      let manifestUrl;
      try {
        manifestUrl = await generateMasterManifestUrl(baseUrl);
      }
      catch (error) {
        if (error.response) {
          if (error.response.status === 401) {
            const message = `User '${error.response.data.userName}' is not authorized to access '${error.response.data.path}'`;
            sendEphemeralSnack(context, LEVELS.ERROR, message);
          }
          else {
            sendEphemeralSnack(context, LEVELS.ERROR, error.response.data);
          }
        }
        else {
          sendEphemeralSnack(context, LEVELS.ERROR, error);
        }

        throw error;
      }

      const mediaProperties = await fetchMediaProperties(new URL(manifestUrl).origin, videoPath, audioPaths, fileOpen);

      const firstProperty = mediaProperties.containerProperties[0];
      if (firstProperty && firstProperty.name === 'Error') {
        context.commit(SET_VIDEO_IS_LOADING, false);
        BusService.videoLoaded.next()

        const message = `Cannot load ${videoPath}${firstProperty.value ? ': ' + firstProperty.value : ''}.`;
        sendEphemeralSnack(context, 'error', message);
        return;
      }

      let qualityLevel = restoreVideoQualityLevel();
      if (isImage(mediaProperties)) {
        qualityLevel = 'Best Quality';
      }

      const quality = {
        currentLevel:     qualityLevel,
        drawAlphaChannel: context.state.isAlphaSimulated
      };

      const controller = generateController(context.state.element, manifestUrl, mediaProperties, quality);
      controller.init();
      controller.on(Events.HLS_ERROR, (name, data) => {
        handleFatalNetworkError(data,controller);
        onHlsError(context, data);
        if(data.response?.code == '403') {
          controller.hls.stopLoad();
          context.commit(SET_VIDEO_HAS_FAILED_LOADING, true);
        }

        context.commit(SET_VIDEO_IS_LOADING, false);
      });

      controller.on(Events.VIDEO_LOADED, () => {
        // Add subtitle files, if any:
        if (subtitlePaths) {
          subtitlePaths
            .filter(path => path != null)
            .forEach(path => context.rootState.videoController.addSidecarSubtitles(path));
        }

        // TODO The file browser dialog needs this, probably need to refactor so that it relies on store state.
        BusService.videoLoaded.next();

        const describedTrackIndex = context.rootState.videoController.audioTracksList.findIndex(t => t.description);
        if (describedTrackIndex >= 0) {
          context.dispatch(SELECT_AUDIO_TRACK, describedTrackIndex);
        }

        context.commit(SET_VIDEO_IS_LOADING, false);
        sendVideoLoadedSnack(videoPath, audioPaths, context);
      });

      controller.hls.on(
        Hls.Events.LEVEL_UPDATED,
        (event, level) => context.commit(SET_VIDEO_IS_LIVE, level.details.live)
      );

      // On 'canplay' because timecode tracks have not loaded on 'Events.VIDEO_LOADED'.
      context.state.element.addEventListener(
        'canplay',
        () => {
          // If the content has video, select the first timecode track:
          if (context.state.properties.videoProperties.length > 0) {
            if (context.state.rotation){
              const ratio = context.state.element.clientHeight / context.state.element.clientWidth;
              context.state.element.style.transform = `scale(${ratio}) rotate(${context.state.rotation}deg)`;
              context.state.element.style.transformOrigin = "center";
            }
            let timeDisplaySource = restoreSelectedTimeDisplaySource();
            if (timeDisplaySource) {
              let parsedTimeDisplaySource = JSON.parse(timeDisplaySource);
              if (parsedTimeDisplaySource.name === 'Time' 
                  || parsedTimeDisplaySource.name === 'Frames' 
                  || parsedTimeDisplaySource.name === 'Normal Play Time') {
                context.dispatch(SELECT_TIME_DISPLAY_SOURCE, parsedTimeDisplaySource)
              } else {
                let sources = this.timecodeSources.map((timecode) => {
                  return {
                    name:  timecode,
                    value: timecode,
                    type:  'timecode',
                  };
                });
                if (sources.find(s => s.name === parsedTimeDisplaySource.name)) {
                  context.dispatch(SELECT_TIME_DISPLAY_SOURCE, parsedTimeDisplaySource)
                } else {
                  context.dispatch(SELECT_FIRST_TIMECODE_TRACK);
                }
              }
            } else {
              context.dispatch(SELECT_FIRST_TIMECODE_TRACK);
            }
          }

          // If the content is audio-only, default to time since timecodes may
          // not make sense in this context.
          else {
            context.dispatch(
              SELECT_TIME_DISPLAY_SOURCE,
              {
                Name:  'Time',
                value: '__seconds__',
                type:  'seconds'
              }
            );
          }

          // Jump to the desired offset:
          if (playheadOffset) {
            context.dispatch(JUMP_TO_FRAME, playheadOffset);
          }
        },
        { once: true }
      );

      context.commit(SET_MEDIA_PROPERTIES, mediaProperties);
      context.commit(SET_VIDEO_CONTROLLER, controller);
      context.commit(SET_VIDEO_PATH, videoPath);
      context.commit(SET_VIDEO_OPEN, fileOpen);
      context.commit(SET_CURRENT_SIDECAR_AUDIO_PATHS, audioPaths);
      context.commit(SET_AUDIO_PATHS_CONFIG, audioPaths);
      context.dispatch(FETCH_PLUGIN_SCHEMAS, new URL(manifestUrl).origin);
      context.dispatch(WATCH_FOR_ANNOTATIONS, new URL(manifestUrl).origin);
      context.dispatch(OBSERVE_INDEXING, { origin: new URL(manifestUrl).origin, path: videoPath });
      context.dispatch(ADD_TO_HISTORY, { path: videoPath, open: fileOpen});
      context.dispatch(START_SCOPES, context.rootState.scopes.type);
      context.commit(SET_VIDEO_IS_PAUSED, true);
      context.commit(SET_VIDEO_FIRST_FRAME, startFrame);
      context.commit(SET_VIDEO_FRAME_COUNT, frameCount);
    },

    async [UNLOAD_VIDEO](context) {
      const managerConnection = ConnectionService.getAutoReconnectConnection('/manager');
      await managerConnection.start();
      try {
        const resultPromise = managerConnection.invoke('Unload', context.rootState.videoPath, context.rootState.videoOpen);
        context.dispatch(RESET_VIDEO_STATE);
        await resultPromise;
        managerConnection.stop()
      } catch (error) {
        managerConnection.stop()
        console.error('Error during Unload operation:', error);
      }
    },

    [RESET_VIDEO_STATE](context) {
      context.dispatch(STOP_SCOPES);

      if (context.rootState.videoController) {
        context.rootState.videoController.destroy();

        context.rootState.licenseService.closeSession(context.rootState.videoPath, "None");

        context.dispatch(STOP_WATCHING_FOR_ANNOTATIONS);

        context.commit(SET_VIDEO_CONTROLLER, undefined);
        context.commit(SET_VIDEO_PATH, undefined);
        context.commit(SET_VIDEO_OPEN, undefined);
        context.commit(SET_VIDEO_CAN_PLAY, false);
        context.commit(SET_MEDIA_PROPERTIES, undefined);
        context.commit(SET_VIDEO_IN, undefined);
        context.commit(SET_VIDEO_OUT, undefined);
        context.commit(SET_VIDEO_FIRST_FRAME, undefined);
        context.commit(SET_VIDEO_FRAME_COUNT, undefined);
        context.commit(SET_PLUGIN_SCHEMAS, []);
        context.commit(SET_TIME_DISPLAY_SOURCE, GLIM_TIMECODE_SOURCE);
        context.commit(SET_VIDEO_IS_LIVE, false);
        context.commit(SET_VIDEO_IS_LOADING, false);
        context.commit(SET_VIDEO_HAS_FAILED_LOADING, false);
        clearMeters();
      }
    },

    [PLAY_VIDEO](context) {
      const inPoint = context.state.inMarker;
      const outPoint = context.state.outMarker || context.state.duration;

      // To make sure playback only occurs inside the looping section (if enabled).
      if (
        context.state.isOnRepeat &&
        inPoint &&
        (
          (context.state.currentTime < inPoint) ||
          (context.state.currentTime > outPoint)
        )
      ) {
        context.dispatch(JUMP_TO_TIME, context.state.inMarker);
      }

      context.state.element.play();
    },

    [PAUSE_VIDEO](context) {
      // Pausing resets most playback flags:
      context.commit(SET_VIDEO_IS_REWINDING, false);
      context.dispatch(CHANGE_PLAYBACK_RATE, 1);

      context.state.element.pause();
      let currentCue = searchForCue(TimecodeService.getTimeCodeTrack(context.state.element).cues, context.state.element.currentTime)
      if (currentCue) {
        context.state.element.currentTime = currentCue.startTime + (currentCue.endTime - currentCue.startTime) / 2;
      }
    },

    [REWIND](context) {
      if ((!context.state.isRewinding) && (context.state.playbackRate === 1)) {
        context.dispatch(PAUSE_VIDEO);
        context.commit(SET_VIDEO_IS_REWINDING, true);
      } else if (context.state.isRewinding) {
        context.dispatch(SPEED_UP);
      } else {
        context.dispatch(SLOW_DOWN);
      }
    },

    [FAST_FORWARD](context) {
      if ((context.state.paused) && (context.state.playbackRate === 1)) {
        context.dispatch(PLAY_VIDEO);
        context.commit(SET_VIDEO_IS_REWINDING, false);
      } else if (context.state.isRewinding) {
        context.dispatch(SLOW_DOWN);
      } else {
        context.dispatch(SPEED_UP);
      }
    },

    [SPEED_UP](context) {
      let target = undefined;
      for (let index = 0; index < PLAYBACK_RATE_LIST.length; index++) {
        if (PLAYBACK_RATE_LIST[index] > context.state.playbackRate) {
          target = PLAYBACK_RATE_LIST[index];
          break;
        }
      }

      if (target) {
        context.dispatch(CHANGE_PLAYBACK_RATE, target);
      }
    },

    [SLOW_DOWN](context) {
      let target = undefined;
      for (let index = PLAYBACK_RATE_LIST.length - 1; index >= 0; index--) {
        if (PLAYBACK_RATE_LIST[index] < context.state.playbackRate) {
          target = PLAYBACK_RATE_LIST[index];
          break;
        }
      }

      if (target) {
        context.dispatch(CHANGE_PLAYBACK_RATE, target);
      }
    },

    [JUMP_BACK_FRAMES](context, frameCount) {
      if (!context.state.canPlay) {
        return;
      }

      const activeCue = TimecodeService.getTimeCodeTrack(context.state.element).activeCues[0];
      const frameLength = activeCue.endTime - activeCue.startTime;
      const jump = frameLength * frameCount;

      this.dispatch(PAUSE_VIDEO);
      this.dispatch(JUMP_TO_TIME, activeCue.startTime - (jump - frameLength / 2));
    },

    [JUMP_FORWARD_FRAMES](context, frameCount) {
      if (!context.state.canPlay || !context.rootState.videoElement) {
        return;
      }

      let currentCue;
      if (context.rootState.videoElement.played.length) {
        currentCue = TimecodeService.getTimeCodeTrack(context.state.element).activeCues[0]
      } else {
        currentCue = TimecodeService.getCurrentTimecodeCue(context.rootState.videoElement);
      }
      if (!currentCue) return;
      const frameLength = currentCue.endTime - currentCue.startTime;
      const jump = frameLength * frameCount;

      this.dispatch(PAUSE_VIDEO);
      this.dispatch(JUMP_TO_TIME, currentCue.endTime + (jump - frameLength / 2));
    },

    [JUMP_TO_BEGINNING](context) {
      context.state.element.currentTime = 0;
    },

    [JUMP_TO_END](context) {
      context.state.element.currentTime = context.state.duration - 0.0001;
    },

    [JUMP_TO_TIME](context, time) {
      let currentCue = searchForCue(TimecodeService.getTimeCodeTrack(context.state.element).cues, time)
      if (currentCue){
        time = currentCue.startTime + (currentCue.endTime - currentCue.startTime) / 2;
        context.state.element.currentTime = time;
      }
      else {
        context.state.element.currentTime = time;
      }
    },

    [JUMP_TO_FRAME](context, frame) {
      const frameDuration = (1 / context.state.framerate);
      const time = (frame / context.state.framerate) + (frameDuration / 2);
      context.state.element.currentTime = time;
    },

    [JUMP_TO_PREVIOUS_MARKER](context) {
      const previousMarkerTime = findPreviousMarkerTime(context);
      if (previousMarkerTime !== undefined) {
        context.dispatch(JUMP_TO_TIME, previousMarkerTime);
      }
    },

    [JUMP_TO_NEXT_MARKER](context) {
      const nextMarkerTime = findNextMarkerTime(context);
      if (nextMarkerTime !== undefined) {
        context.dispatch(JUMP_TO_TIME, nextMarkerTime);
      }
    },

    [JUMP_TO_PREVIOUS_IFRAME](context) {
      const previousMarkerTime = findPreviousIFrameTime(context);
      if (previousMarkerTime !== undefined) {
        context.dispatch(JUMP_TO_TIME, previousMarkerTime);
      }
    },

    [JUMP_TO_NEXT_IFRAME](context) {
      const nextIFrameTime = findNextIFrameTime(context);
      if (nextIFrameTime !== undefined) {
        context.dispatch(JUMP_TO_TIME, nextIFrameTime);
      }
    },

    [SELECT_TIME_DISPLAY_SOURCE](context, source) {
      context.commit(SET_TIME_DISPLAY_SOURCE, source);

      const sourceName = context.rootState.timeDisplaySource.name;
      context.commit(SET_VIDEO_FIRST_TIMECODE, getFirstTimecode(context.state.element, sourceName));
      context.commit(SET_VIDEO_LAST_TIMECODE, getLastTimecode(context.state.element, sourceName));
    },

    [SELECT_FIRST_TIMECODE_TRACK](context) {
      const timecodeSources = getTimecodeSources(context.state.element);
      if (timecodeSources && timecodeSources.length > 0) {
        context.dispatch(
          SELECT_TIME_DISPLAY_SOURCE,
          {
            name:  timecodeSources[0].track,
            value: timecodeSources[0].track,
            type:  'timecode'
          }
        );
      }
    },

    [MARK_IN](context) {
      const newInTime = context.state.element.currentTime;
      context.commit(SET_VIDEO_IN, newInTime);
    },

    [CLEAR_MARK_IN](context) {
      context.commit(SET_VIDEO_IN, undefined);
    },

    [MARK_OUT](context) {
      const newOutTime = context.state.element.currentTime;
      context.commit(SET_VIDEO_OUT, newOutTime);
    },

    [CLEAR_MARK_OUT](context) {
      context.commit(SET_VIDEO_OUT, undefined);
    },

    [TOGGLE_REPEAT](context) {
      context.commit(SET_VIDEO_IS_ON_REPEAT, !context.state.isOnRepeat);
    },

    [CHANGE_PLAYBACK_RATE](context, rate) {
      context.state.element.playbackRate = rate;
      context.commit(SET_VIDEO_PLAYBACK_RATE, context.state.element.playbackRate);
    },

    [TOGGLE_MUTE](context) {
      context.state.element.muted = !context.state.isMuted;
      context.commit(SET_VIDEO_IS_MUTED, context.state.element.muted);
      storeIsMuted(context.state.element.muted);
    },

    [ADJUST_VOLUME](context, volume) {
      context.state.element.volume = volume;
      storeVolume(volume);
    },

    [SHOW_FULLSCREEN](context) {
      if (!document.fullscreenEnabled || !context.state.fullscreenElement) {
        return;
      }

      context.state.fullscreenElement.requestFullscreen();
    },

    [EXIT_FULLSCREEN]() {
      document.exitFullscreen();
    },

    [SELECT_OVERLAYS](context, overlays) {
      storeOverlays(overlays);
      context.commit(SET_VIDEO_OVERLAYS, overlays);
    },

    [SIMULATE_ALPHA](context, isToggled) {
      storeSimulateAlpha(isToggled);
      context.rootState.videoController.setVideoQuality(restoreVideoQualityLevel(), isToggled);

      context.commit(SET_VIDEO_SIMULATE_ALPHA, isToggled);
    },

    async [ADD_SUBTITLE_SOURCE](context, path) {
      await context.rootState.videoController.addSidecarSubtitles(path);
      context.commit(SET_FILE_BROWSER_IS_OPEN, false);
      context.commit(SET_SNACK, {
        message: `Added subtitles from ${path}.`,
        level:   'success',
      });
    },

    [REMOVE_SUBTITLE_SOURCE](context, sourceId) {
      const controller = context.rootState.videoController;

      if (controller.currentSubtitles === sourceId) {
        controller.setSubtitleOff();
      }

      controller.deleteSidecarSubtitles(sourceId);
    },

    [SELECT_SUBTITLE_SOURCE](context, sourceId) {
      const controller = context.rootState.videoController;

      // Clear current selection:
      controller.sidecarSubtitles.forEach((s) => s.trackElement.remove());

      // Set selection:
      if (controller.sidecarSubtitles.find((s) => s.id === sourceId)) {
        controller.appendSidecarSubtitleTrack(sourceId);
        controller.setSidecarSubtitles(sourceId);
      } else {
        controller.setHlsSubtitles(sourceId);
      }

      storeSubtitleSource(sourceId);
    },

    [SELECT_NEXT_SUBTITLE_SOURCE](context) {
      if (!context.rootState.videoController?.currentSubtitles) {
        return;
      }
      
      const allTrackIds = [
        'Off',
        ...context.rootState.videoController.availableServices,
        ...context.rootState.videoController.sidecarSubtitles.map(s => s.id)
      ];

      const currentTrackIndex = allTrackIds.findIndex(t => t === context.rootState.videoController.currentSubtitles);
      const nextTrackId = allTrackIds[(currentTrackIndex + 1) % allTrackIds.length];

      context.dispatch(SELECT_SUBTITLE_SOURCE, nextTrackId);
    },

    [SELECT_PERSISTED_SUBTITLE_SOURCE](context) {
      const controller = context.rootState.videoController;
      const sourceId = restoreSubtitleSource();

      if (controller.hls.subtitleTrack !== -1 && sourceId === 'Off'){
        controller.setSubtitleOff();
        return;
      }
      if (controller.currentSubtitles === sourceId) {
        return;
      }

      if (controller.sidecarSubtitles.find((s) => s.id === sourceId)) {
        controller.sidecarSubtitles.forEach((s) => s.trackElement.remove());
        controller.appendSidecarSubtitleTrack(sourceId);
      } else if (controller.availableServices.find((s) => s === sourceId)) {
        controller.setHlsSubtitles(sourceId);
      } else {
        controller.setSubtitleOff();
      }
    },

    [TURN_OFF_SUBTITLES](context) {
      if (!context.rootState.videoController) {
        return;
      }

      const controller = context.rootState.videoController;
      controller.setSubtitleOff();

      storeSubtitleSource('Off');
    },

    [SELECT_WAVEFORM_MARKS](context, selectedWaveformMarks) {
      context.commit(SET_SELECTED_WAVEFORM_MARKS, selectedWaveformMarks);
      storeSelectedWaveformMarks(selectedWaveformMarks);
    },

    [SELECT_ROTATION](context, rotation) {
      context.commit(SET_ROTATION, rotation);
    },


    async [COPY_MEDIA_PATH](context) {
      if (!context.rootState.videoPath) {
        return;
      }

      const subtitles = [];

      // Make sure the currently selected track is first so it gets selected when the link is used:
      const sidecarSubs = [...context.rootState.videoController.sidecarSubtitles];
      const currentSubtitleSource = context.rootState.videoController.currentSubtitles;
      const sidecarSubIndex = sidecarSubs.findIndex(s => s.id === currentSubtitleSource);
      if (sidecarSubIndex >= 0) {
        subtitles.push(sidecarSubs[sidecarSubIndex].path);
        sidecarSubs.splice(sidecarSubIndex, 1);
      } else {
        subtitles.push(currentSubtitleSource);
      }

      // Include all remaining sidecar sub files:
      sidecarSubs.forEach(s => subtitles.push(s.path));

      let startFrame = undefined;
      if (context.state.inMarker) {
        startFrame = Math.round(context.state.inMarker * context.state.framerate);
      }

      let frameCount = undefined;
      if (context.state.outMarker) {
        frameCount = Math.round((context.state.outMarker - (context.state.inMarker || 0)) * context.state.framerate);
      }

      const cliplink = generateClipLink(
        context.rootState.videoPath,
        context.rootState.videoController.currentAudioTrack,
        context.rootState.currentSidecarAudioPaths,
        startFrame,
        frameCount,
        subtitles
      );

      if (navigator.clipboard) {
        navigator.clipboard.writeText(cliplink);
      } else {
        copyUsingTextArea(cliplink);
      }

      sendEphemeralSnack(context, LEVELS.INFO, 'Clip link copied to clipboard.');
    },
  },

  // #endregion Actions
};

function pollVideoElement(timestamp, context, element) {
  updateTime(context, element);
  updateTimecode(context, element);
  updateFrameMetadata(context, element);
  checkForLooping(context);
  checkForRewinding(context, timestamp);

  requestAnimationFrame(timestamp => pollVideoElement(timestamp, context, element));
}

function updateTime(context, element) {
  context.commit(SET_VIDEO_CURRENT_TIME, element.currentTime);
}

function updateTimecode(context, element) {
  const sources = getTimecodeSources(element);
  if (!sources) {
    return;
  }

  let timecode = sources.find((s) => s.track === context.rootState.timeDisplaySource?.name);
  if (!timecode) {
    timecode = sources[sources.length - 1]; // There is always 1 source.
  }

  const sourceNames = sources.map(s => s.track);
  if (context.state.timecodeSources.toString() !== sourceNames.toString()) {
    context.commit(
      SET_VIDEO_TIMECODE_SOURCES,
      sources.map((s) => s.track)
    );
  }

  context.commit(
    SET_VIDEO_CURRENT_TIMECODE,
    {
      timecode: timecode.value,
      frame:    Number(sources[sources.length - 1].frame)      // Only the generated timecode has frame info:
    }
  );
}

function updateFrameMetadata(context, element) {
  const metadataTrack = TimecodeService.getTimeCodeTrack(element);
  if (!metadataTrack) {
    context.commit(SET_FRAME_METADATA, []);
    return;
  }

  const metadataCue = MetadataService.searchForCue(metadataTrack.cues, element.currentTime);
  if (!metadataCue) {
    context.commit(SET_FRAME_METADATA, []);
    return;
  }

  const metadataElement = metadataCue.value.info.find((e) => e.name === 'metadata');
  if (!metadataElement) {
    context.commit(SET_FRAME_METADATA, []);
    return;
  }

  context.commit(SET_FRAME_METADATA, metadataElement.children);
}

function checkForLooping(context) {
  if (
    !context.state.isOnRepeat ||  // Only if repeat is enabled
    context.state.paused          // And media is playing
  ) {
    return;
  }

  /*
   * If we are looping over the full clip, it is not sufficient to only look at
   * the duration as reported by the video element. Due to the fact that the audio
   * needs priming samples, it may have a different duration than the video track,
   * which causes the video element to not be able to natively loop or trigger
   * video-end events.
   *
   * To mitegate this, we subtract a priming delay from the video duration.
   */

  const inMarker = context.state.inMarker || 0;
  const outMarker = context.state.outMarker || context.state.element.duration - AUDIO_PRIMING_DELAY_IN_SECONDS;

  // Don't loop if we are not there yet:
  if (context.state.element.currentTime < outMarker) {
    return;
  }

  context.dispatch(JUMP_TO_TIME, inMarker);
}

const REWIND_INTERVAL_IN_MS         =  40;
let lastRewindTimestamp             =  0;

function checkForRewinding(context, timestamp) {
  if (
    (!context.state.isRewinding) ||
    ((timestamp - lastRewindTimestamp) < REWIND_INTERVAL_IN_MS)
  ) {
    return;
  }

  const jumpAmount = (REWIND_INTERVAL_IN_MS / 1000) * context.state.playbackRate;
  let jumpToPoint = context.state.element.currentTime - jumpAmount;

  if (context.state.isOnRepeat) {
    const inPoint = context.state.inMarker || 0;
    const outPoint = context.state.outMarker || context.state.element.duration;

    jumpToPoint = Math.max(jumpToPoint, inPoint);
    if (jumpToPoint === inPoint) {
      jumpToPoint = outPoint;
    }
  } else {
    jumpToPoint = Math.max(jumpToPoint, 0);
    if (jumpToPoint === 0) {
      context.dispatch(PAUSE_VIDEO);
    }
  }

  context.dispatch(JUMP_TO_TIME, jumpToPoint);

  lastRewindTimestamp = timestamp;
}
